import { en } from './en';
import { zhTW } from './zh-TW';

export type Locale = 'zh-TW' | 'en';
export const dictionaries = { en, 'zh-TW': zhTW };
export type MessageKey = keyof typeof en;

const zhWeekdayMap = {
	Mon: '一',
	Tue: '二',
	Wed: '三',
	Thu: '四',
	Fri: '五',
	Sat: '六',
	Sun: '日',
} as const;

export interface FormatNodeTimestampOptions {
	originTimeZone?: string | null;
	viewerTimeZone?: string;
}

export function t(locale: Locale, key: MessageKey): string {
	return dictionaries[locale]?.[key] ?? en[key] ?? key;
}

export function formatNodeTimestamp(
	isoString: string,
	locale: Locale,
	options: FormatNodeTimestampOptions = {},
): string {
	const date = new Date(isoString);

	if (Number.isNaN(date.getTime())) {
		return isoString;
	}

	const viewerTimeZone = options.viewerTimeZone ?? resolveViewerTimeZone();
	const originTimeZone = options.originTimeZone ?? 'UTC';
	const originLabel = formatOriginTimeZoneLabel(date, locale, originTimeZone);

	if (locale === 'zh-TW') {
		return `${formatZhLocalDateTime(date, viewerTimeZone)}（${formatZhWeekday(date, viewerTimeZone)}，${t(locale, 'localTimeLabel')}；${t(locale, 'originTimeZoneLabel')}：${originLabel}）`;
	}

	return `${formatEnLocalDateTime(date, viewerTimeZone)} (${t(locale, 'localTimeLabel')}; ${t(locale, 'originTimeZoneLabel')}: ${originLabel})`;
}

function formatZhLocalDateTime(date: Date, timeZone?: string): string {
	const parts = new Intl.DateTimeFormat('en-US', {
		...timeZoneOption(timeZone),
		year: 'numeric',
		month: 'numeric',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		hourCycle: 'h23',
	}).formatToParts(date);

	return `${partValue(parts, 'year')}年${Number(partValue(parts, 'month'))}月${Number(partValue(parts, 'day'))}日 ${partValue(parts, 'hour')}:${partValue(parts, 'minute')}`;
}

function formatEnLocalDateTime(date: Date, timeZone?: string): string {
	return new Intl.DateTimeFormat('en-US', {
		...timeZoneOption(timeZone),
		weekday: 'short',
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		hourCycle: 'h23',
	}).format(date);
}

function formatZhWeekday(date: Date, timeZone?: string): string {
	const weekday = new Intl.DateTimeFormat('en-US', {
		...timeZoneOption(timeZone),
		weekday: 'short',
	}).format(date);

	return `週${zhWeekdayMap[weekday as keyof typeof zhWeekdayMap] ?? weekday}`;
}

function formatOriginTimeZoneLabel(date: Date, locale: Locale, timeZone: string): string {
	const normalizedTimeZone = timeZone === 'Etc/UTC' ? 'UTC' : timeZone;
	const label = localizedTimeZoneLabel(locale, normalizedTimeZone);
	const offset = formatTimeZoneOffset(date, normalizedTimeZone);

	if (label === offset) {
		return label;
	}

	return `${label} ${offset}`;
}

function localizedTimeZoneLabel(locale: Locale, timeZone: string): string {
	if (timeZone === 'UTC') {
		return 'UTC';
	}

	if (timeZone === 'Asia/Taipei') {
		return t(locale, 'timeZoneAsiaTaipei');
	}

	return timeZone;
}

function formatTimeZoneOffset(date: Date, timeZone: string): string {
	if (timeZone === 'UTC') {
		return 'UTC';
	}

	try {
		const offset = new Intl.DateTimeFormat('en-US', {
			timeZone,
			hour: '2-digit',
			minute: '2-digit',
			timeZoneName: 'longOffset',
		}).formatToParts(date).find((part) => part.type === 'timeZoneName')?.value;

		if (!offset || offset === 'GMT') {
			return 'UTC';
		}

		return offset.replace(/^GMT/, 'UTC');
	} catch {
		return 'UTC';
	}
}

function partValue(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
	return parts.find((part) => part.type === type)?.value ?? '';
}

function resolveViewerTimeZone(): string | undefined {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone;
	} catch {
		return undefined;
	}
}

function timeZoneOption(timeZone?: string): { timeZone?: string } {
	return timeZone ? { timeZone } : {};
}
